/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useEffect, useState } from "react";
import { Alert, AlertColor, Snackbar } from "@mui/material";
import { AppContext } from "utils/AppContext";
import { GameServersTable } from "./GameServersTable";
import { GameServerSpawnPopup } from "./GameServerSpawnPopup";
import {
  GameServerType,
  IGameServer,
  IGameServerWithNeighborhood,
  useGameServerService,
} from "../../services/backend/GameServerService";
import { IServerImage, useServerImageService } from "../../services/registry/ServerImageService";
import { YesNoCancelPopup } from "../../components/YesNoCancelPopup";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import { LogPopup } from "../../components/LogPopup";
import { INeighborhood, useNeighborhoodService } from "../../services/backend/NeighborhoodService";
import { useSearchParams } from "react-router-dom";
import { GameServerNeighborhoodData } from "./GameServerNeighborhoodData";
import { isScalableEnvironment } from "services/registry/EnvironmentService";

const GameServers = () => {
  const gameServerService = useGameServerService();
  const neighborhoodService = useNeighborhoodService();
  const serverImageService = useServerImageService();
  const [searchParams, _setSearchParams] = useSearchParams();

  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [rows, setRows] = useState<IGameServerWithNeighborhood[]>([]);
  const [selectedItem, setSelectedItem] = useState<IGameServerWithNeighborhood | undefined>(undefined);
  const [gameServerSpawnPopupVisible, setGameServerSpawnPopupVisible] = useState<boolean>(false);
  const [neighborhoodDataVisible, setNeighborhoodDataVisible] = useState<boolean>(false);
  const [terminatePopupVisible, setTerminatePopupVisible] = useState<boolean>(false);
  const [deleteNeighborhoodPopupVisible, setDeleteNeighborhoodPopupVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<string | undefined>(undefined);
  const [environmentName, setEnvironmentName] = useState<string | undefined>(undefined);
  const [serverImages, setServerImages] = useState<IServerImage[]>([]);

  useEffect(() => {
    const serverId = searchParams.get("serverId");
    const containerName = searchParams.get("containerName");
    if (serverId && rows.length > 0) {
      const server = rows.find((row) => row.id === serverId);
      if (server) {
        setSelectedItem(server);
      }
    } else if (containerName && rows.length > 0) {
      const server = rows.find((row) => row.containerName === containerName);
      if (server) {
        setSelectedItem(server);
      }
    }
  }, [searchParams, rows]);

  const fetchRows = async () => {
    try {
      const { list: gameServerList } = await gameServerService.list();
      const neighborhoodList: INeighborhood[] = [];
      let cursor = "0";
      do {
        const res = await neighborhoodService.listNeighborhoods();
        neighborhoodList.push(...res.list);
        cursor = res.cursor;
      } while (cursor && cursor !== "0");

      const gs = gameServerList.map((server: IGameServer) => {
        const nb = neighborhoodList.find((nb) => nb.id === server.neighborhoodId);

        return { ...server, neighborhoodData: nb };
      });

      setRows(gs as IGameServerWithNeighborhood[]);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not fetch the list of game servers.");
      setAlertSeverity("error");
    }
  };

  const fetchServerImages = async () => {
    try {
      const { list } = await serverImageService.listServerImages();
      setServerImages(list as IServerImage[]);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not fetch the list of server images.");
      setAlertSeverity("error");
    }
  };

  useEffect(() => {
    (async () => fetchServerImages())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshButtonClickHandler = async () => {
    setLoading(true);
    await fetchRows();
    setLoading(false);
  };

  const spawnButtonClickHandler = () => {
    setGameServerSpawnPopupVisible(true);
  };

  const gameServerSpawnPopupCancelHandler = () => {
    setGameServerSpawnPopupVisible(false);
  };

  const gameServerSpawnPopupActionHandler = async (
    containerName: string,
    serverType?: GameServerType,
    serverImage?: IServerImage,
    neighborhoodId?: string,
    townId?: string,
    expeditionId?: string,
    cityId?: string,
  ) => {
    try {
      setLoading(true);
      setGameServerSpawnPopupVisible(false);
      await gameServerService.spawn(
        containerName,
        serverType,
        serverImage?.id,
        neighborhoodId,
        townId,
        expeditionId,
        cityId,
      );
      await fetchRows();
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not spawn the new game server.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const terminatePopupNoHandler = () => {
    setTerminatePopupVisible(false);
  };

  const terminatePopupYesHandler = async () => {
    if (!selectedItem) {
      return;
    }
    try {
      setLoading(true);
      setTerminatePopupVisible(false);
      await gameServerService.terminate(selectedItem.id);
      await fetchRows();
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not terminate the selected game server.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const deleteNeighborhoodPopupCancelHandler = () => {
    setDeleteNeighborhoodPopupVisible(false);
  };

  const deleteNeighborhoodPopupNoHandler = async () => {
    if (!selectedItem) {
      return;
    }
    try {
      setLoading(true);
      setDeleteNeighborhoodPopupVisible(false);
      await gameServerService.terminate(selectedItem.id);
      await fetchRows();
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not terminate the selected game server.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const deleteNeighborhoodPopupYesHandler = async () => {
    if (!selectedItem || !selectedItem.neighborhoodId) {
      return;
    }
    try {
      setLoading(true);
      setDeleteNeighborhoodPopupVisible(false);
      await neighborhoodService.delete(selectedItem.neighborhoodId);
      await fetchRows();
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not delete the neighborhood.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const neighborhoodDataMenuClick = () => {
    setNeighborhoodDataVisible(true);
  };

  const terminateMenuClickHandler = () => {
    if (selectedItem?.type === GameServerType.NEIGHBORHOOD && selectedItem?.neighborhoodId) {
      setDeleteNeighborhoodPopupVisible(true);
    } else {
      setTerminatePopupVisible(true);
    }
  };

  const showLogViewClickHandler = () => {
    window.open(`${window.location.origin}/logging/?name=${selectedLog}`);
  };

  const closeLogPopupClickHandler = () => {
    setSelectedLog(undefined);
  };

  const showLogsClickHandler = async () => {
    if (!selectedItem) {
      return;
    }

    if (isScalableEnvironment(context?.selectedEnvironment?.stage as number)) {
      const containerId = selectedItem.id;
      const datadogLogsUrl = `https://app.datadoghq.com/logs?query=source:agones-game-server game-server-id:${containerId}`;
      window.open(encodeURI(datadogLogsUrl));
    } else {
      setSelectedLog(`${environmentName}_gs_${selectedItem.id}_${selectedItem.containerName}`);
    }
  };

  const datadogDashboardClickHandler = async () => {
    if (!selectedItem) {
      return;
    }
    try {
      setLoading(true);
      const datadogDashboardUrl = await gameServerService.getDatadogDashboardUrl(
        selectedItem.id,
        selectedItem.containerName,
      );
      window.open(datadogDashboardUrl.url);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not open datadog dashboard for game-server.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const getCloudWatchGraphParam = (containerId: string, region: string): string => {
    // List of metrics to display on the graph
    const metrics = [
      "ConnectedClients",
      "ActivePlayers",
      "Jitter",
      "MinimumPlayerSession",
      "MaximumPlayerSession",
      "PercentagePackageLoss",
      "ClientsMaximumLatency",
      "AveragePlayerSession",
      "UpstreamBandwidth",
      "ClientsAverageLatency",
      "CpuUsagePercent",
      "MemoryUsageMB",
      "DownstreamBandwidth",
    ];
    // Define the metrics that are being plotted, first one is a special case
    const firstMetric = `~(~unknown~'${metrics[0]}~'GameServer~'${containerId})`;
    const otherMetrics = metrics
      .slice(1)
      .map((metric) => `~(~'.~'${metric}~'.~'.)`)
      .join("");

    // Build the graph param
    return `~(view~'timeSeries~stacked~false~metrics~(${firstMetric}${otherMetrics})~region~'${region})`;
  };

  const getCloudWatchQueryParam = (containerId: string): string => {
    // Define the search query for the game server id
    const queryValue = `~'{unknown,GameServer} GameServer="${containerId}"`;
    // Comply with the specific encoding that Cloudwatch requires
    return queryValue
      .replace(/{/g, "*7b")
      .replace(/,/g, "*2c")
      .replace(/}/g, "*7d")
      .replace(/ /g, "*20")
      .replace(/"/g, "*22");
  };

  const cloudWatchDashboardClickHandler = () => {
    if (!selectedItem) {
      return;
    }
    if (!isScalableEnvironment(context?.selectedEnvironment?.stage as number)) {
      const containerId = selectedItem.id;
      const awsRegion = "us-east-1";
      const baseUrl = `https://${awsRegion}.console.aws.amazon.com/cloudwatch/home`;
      const graphParam = getCloudWatchGraphParam(containerId, awsRegion);
      const queryParam = getCloudWatchQueryParam(containerId);
      const cloudWatchLogsUrl = `${baseUrl}?region=${awsRegion}#metricsV2?graph=${graphParam}&query=${queryParam}`;
      window.open(cloudWatchLogsUrl);
    }
  };

  const stopMenuClickHandler = async () => {
    if (!selectedItem) {
      return;
    }
    try {
      setLoading(true);
      await gameServerService.stop(selectedItem.id);
      await fetchRows();
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not stop the selected game server.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const restartMenuClickHandler = async () => {
    if (!selectedItem) {
      return;
    }
    try {
      setLoading(true);
      await gameServerService.restart(selectedItem.id);
      await fetchRows();
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not restart the selected game server.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  const context = React.useContext(AppContext);
  useEffect(() => {
    if (context) {
      context.setTitle("Game Servers");
      context.setTargetAPI("environment");
      if (context.selectedEnvironment) {
        setEnvironmentName(context.selectedEnvironment.name);
        (async () => fetchRows())();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context]);

  return (
    <>
      <GameServersTable
        rows={rows}
        serverImages={serverImages}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        onSpawnButtonClick={spawnButtonClickHandler}
        onRefreshButtonClick={refreshButtonClickHandler}
        onNeighborhoodDataMenuClick={neighborhoodDataMenuClick}
        onTerminateMenuClick={terminateMenuClickHandler}
        onStopMenuClick={stopMenuClickHandler}
        onRestartMenuClick={restartMenuClickHandler}
        onShowLogsMenuClickHandlerMenuClick={showLogsClickHandler}
        onDatadogDashboardMenuClick={datadogDashboardClickHandler}
        onCloudWatchDashboardMenuClick={cloudWatchDashboardClickHandler}
      />
      {selectedLog !== undefined && (
        <LogPopup
          visible={true}
          logName={selectedLog}
          onClose={closeLogPopupClickHandler}
          onShowLogView={showLogViewClickHandler}
        />
      )}
      <GameServerNeighborhoodData
        title={`Neighborhood Data for ${selectedItem?.containerName}`}
        neighborhoodData={selectedItem?.neighborhoodData}
        open={neighborhoodDataVisible}
        onClose={() => setNeighborhoodDataVisible(false)}
      ></GameServerNeighborhoodData>
      <GameServerSpawnPopup
        visible={gameServerSpawnPopupVisible}
        onCancel={gameServerSpawnPopupCancelHandler}
        onAction={gameServerSpawnPopupActionHandler}
      />
      <YesNoCancelPopup
        visible={terminatePopupVisible}
        title={"Terminate Game Server"}
        message={`Are you sure you want to terminate the Game Server named <b>${selectedItem?.containerName}</b>?<br/>
        If you terminate this Game Server, a long cold-start will be required to restart it.`}
        onNo={terminatePopupNoHandler}
        onYes={terminatePopupYesHandler}
      />
      <YesNoCancelPopup
        visible={deleteNeighborhoodPopupVisible}
        title={"Terminate Neighborhood Game Server"}
        message={`Do you wish to delete the Neighborhood associated with the Game Server named <b>${selectedItem?.containerName}</b>?<br/>
        This action is irreversible. Hit "No" to only terminate the Game Server.`}
        onYes={deleteNeighborhoodPopupYesHandler}
        onNo={deleteNeighborhoodPopupNoHandler}
        onCancel={deleteNeighborhoodPopupCancelHandler}
      />
      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
      {loading && <LoadingOverlay />}
    </>
  );
};

export default GameServers;
